import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import {
  EXPORT_LOGO_ROW_HEIGHT,
  EXPORT_LOGO_SIZE,
  ExportBrandingService,
} from './export-branding.service';

/** One column of an exported sheet. `key` indexes into each row object. */
export interface ExcelExportColumn {
  header: string;
  key: string;
  /** Column width in Excel character units. */
  width?: number;
}

/**
 * A range to merge, addressed in **data coordinates**: row 0 is the first data
 * row and column 0 the first column, both ignoring the logo and header rows.
 * Keeping call sites in data coordinates means they never have to know how many
 * rows the branding occupies — the classic off-by-one when a logo is added.
 */
export interface ExcelExportMerge {
  startRow: number;
  endRow: number;
  startCol: number;
  endCol: number;
}

export interface ExcelExportOptions {
  /** File name, without the .xlsx extension. */
  fileName: string;
  sheetName: string;
  columns: ExcelExportColumn[];
  rows: any[];
  merges?: ExcelExportMerge[];
  /** Mirror the sheet for Arabic. */
  rightToLeft?: boolean;
}

/** Sheet row (1-based) of the first data row: row 1 is the logo, row 2 the headers. */
const FIRST_DATA_ROW = 3;

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Builds and downloads a single-sheet .xlsx with the brand logo above the header
 * row.
 *
 * This exists because SheetJS — which most exports here used to use — cannot
 * embed images at all, and neither can Excel 2003 SpreadsheetML. Only OOXML can,
 * so branded exports have to go through ExcelJS. Rather than repeat that setup at
 * every call site, list your columns and rows and let this handle the workbook.
 *
 * ExcelJS is imported dynamically to keep it out of the initial bundle.
 */
@Injectable({
  providedIn: 'root',
})
export class ExcelExportService {
  constructor(private exportBranding: ExportBrandingService) {}

  async downloadSheet(options: ExcelExportOptions): Promise<void> {
    const excelJsModule: any = await import('exceljs');
    const ExcelJS = excelJsModule.default ?? excelJsModule;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(options.sheetName, {
      views: [{ rightToLeft: !!options.rightToLeft }],
    });

    // Declaring columns without a header lets us address cells by key while
    // keeping row 1 free for the logo and writing the header row ourselves.
    worksheet.columns = options.columns.map(column => ({
      key: column.key,
      width: column.width,
    }));

    await this.stampLogo(workbook, worksheet);

    const headerRow = worksheet.getRow(2);
    options.columns.forEach((column, index) => {
      const cell = headerRow.getCell(index + 1);
      cell.value = column.header;
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF231F20' },
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });
    headerRow.commit();

    options.rows.forEach((row, rowIndex) => {
      const sheetRow = worksheet.getRow(rowIndex + FIRST_DATA_ROW);
      options.columns.forEach((column, columnIndex) => {
        const cell = sheetRow.getCell(columnIndex + 1);
        cell.value = row[column.key] ?? '';
        // Cells routinely hold newline-separated bullet lists. Without wrapText
        // Excel shows only the text up to the first newline and hides the rest
        // until the cell is opened for editing. Top alignment keeps a value at
        // the top of its merged block rather than floating to the bottom.
        cell.alignment = { vertical: 'top', wrapText: true };
      });
      sheetRow.commit();
    });

    (options.merges ?? []).forEach(merge => {
      worksheet.mergeCells(
        merge.startRow + FIRST_DATA_ROW,
        merge.startCol + 1,
        merge.endRow + FIRST_DATA_ROW,
        merge.endCol + 1
      );
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: XLSX_MIME }), `${options.fileName}.xlsx`);
  }

  /**
   * Place the logo in a reserved first row. A logo that fails to load costs the
   * user a logo, not their export.
   */
  private async stampLogo(workbook: any, worksheet: any): Promise<void> {
    try {
      const logoId = workbook.addImage({
        buffer: await this.exportBranding.getLogoBuffer(),
        extension: 'png',
      });
      worksheet.getRow(1).height = EXPORT_LOGO_ROW_HEIGHT;
      worksheet.addImage(logoId, {
        tl: { col: 0, row: 0 },
        ext: { width: EXPORT_LOGO_SIZE.width, height: EXPORT_LOGO_SIZE.height },
        editAs: 'oneCell',
      });
    } catch (error) {
      console.error('Could not embed export logo:', error);
    }
  }
}
